import React, { useState, useEffect } from "react";
import { GrowthResult } from "./GrowthQuiz";
import {
  Lightbulb,
  ConnectWithoutContact,
  EmojiEvents,
  TrendingUp,
  Star,
  ArrowForward,
  CheckCircle,
  Warning,
  Info,
} from "@mui/icons-material";

interface GrowthResultsProps {
  results: GrowthResult[];
  onRestart: () => void;
}

const GrowthResults: React.FC<GrowthResultsProps> = ({
  results,
  onRestart,
}) => {
  const [animationStep, setAnimationStep] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const timer = setInterval(() => {
      setAnimationStep((prev) => Math.min(prev + 1, 3));
    }, 300);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  // Calculate total scores for each dimension
  const totalScores = results.reduce(
    (acc, result) => ({
      seeIt: acc.seeIt + result.scores.seeIt,
      bridgeIt: acc.bridgeIt + result.scores.bridgeIt,
      beIt: acc.beIt + result.scores.beIt,
    }),
    { seeIt: 0, bridgeIt: 0, beIt: 0 }
  );

  // Determine primary focus area
  const maxScore = Math.max(
    totalScores.seeIt,
    totalScores.bridgeIt,
    totalScores.beIt
  );
  let primaryFocus = "";
  let focusColor = "";
  let focusGradient = "";

  if (totalScores.seeIt === maxScore) {
    primaryFocus = "SEE IT";
    focusColor = "#c9a750";
    focusGradient = "#c9a750";
  } else if (totalScores.bridgeIt === maxScore) {
    primaryFocus = "BRIDGE IT";
    focusColor = "#c9a750";
    focusGradient = "#c9a750";
  } else {
    primaryFocus = "BE IT";
    focusColor = "#c9a750";
    focusGradient = "#c9a750";
  }

  const getFocusDetails = () => {
    if (primaryFocus === "SEE IT") {
      return {
        icon: <Lightbulb style={{ fontSize: "4rem" }} />,
        title: "SEE IT: Gaining Clarity & Vision",
        subtitle: "Your Primary Focus Area",
        description:
          "You need a clearer picture of where you're heading and why. Right now, you may feel uncertain about the best path forward. Getting crystal clear on your direction will unlock your next level of growth.",
        symptoms: [
          "Unsure about which direction to take your business",
          "Hard to explain your vision to your team or customers",
          "Stuck when making important decisions",
          "Not confident about your long-term goals",
          "Difficult to get others excited about where you're going",
        ],
        impacts: [
          "Missing opportunities because decisions take too long",
          "Team feels unmotivated without a clear destination",
          "Everything feels equally important, nothing gets prioritized",
          "Hard to attract great people without a compelling vision",
          "Resources scattered across too many directions",
        ],
        solutions: [
          "Vision Workshop: Set aside time to create a clear 3-5 year vision",
          "Talk to Key People: Get input from important stakeholders to build alignment",
          "Market Research: Look at trends and customer needs to guide your direction",
          "Find Your Why: Understand the deeper purpose behind your work",
          "Plan Scenarios: Explore different paths to build confidence in your choice",
        ],
        nextSteps: [
          {
            step: "Write Your Vision",
            action:
              "In 2 weeks, write a clear vision that answers: Where are you going? Why does it matter? What will success look like?",
          },
          {
            step: "Pick Your Priorities",
            action:
              "Choose 3-5 key priorities that support your vision and stop doing things that don't align",
          },
          {
            step: "Share the Vision",
            action:
              "Create a plan to share your vision with everyone, making sure they understand their role",
          },
          {
            step: "Track Progress",
            action:
              "Pick 3-5 measures to track progress and review them every quarter",
          },
        ],
        growth_insight:
          "When you're clear on where you're going and why it matters, everything becomes easier. Your vision becomes the guide for every decision and inspires your team to take action.",
      };
    } else if (primaryFocus === "BRIDGE IT") {
      return {
        icon: <ConnectWithoutContact style={{ fontSize: "4rem" }} />,
        title: "BRIDGE IT: Achieving Alignment & Execution",
        subtitle: "Your Primary Focus Area",
        description:
          "You know where you want to go, but getting everyone moving in the same direction is the challenge. Your team isn't working together as well as they could, and good ideas aren't turning into results fast enough.",
        symptoms: [
          "Plans exist but execution feels scattered",
          "Different teams working against each other",
          "Good people not working together toward the same goals",
          "Great ideas that never seem to happen",
          "Gap between knowing what to do and actually getting it done",
        ],
        impacts: [
          "Effort wasted on competing priorities instead of focused work",
          "Good employees frustrated when their work doesn't lead to results",
          "Time and money lost due to miscommunication and rework",
          "Customers getting inconsistent experiences",
          "Team losing trust when promises aren't kept",
        ],
        solutions: [
          "Alignment System: Connect everyone's work to the bigger goals with clear accountability",
          "Cross-Team Meetings: Bring different groups together regularly to solve problems",
          "Process Mapping: Document and streamline how work gets done",
          "Communication Rhythm: Create regular check-ins for sharing updates and making decisions",
          "Progress Dashboard: Make results visible to everyone in real-time",
        ],
        nextSteps: [
          {
            step: "Alignment Meeting",
            action:
              "In 30 days, meet with leaders to map all current work against your priorities and find gaps",
          },
          {
            step: "Clear Ownership",
            action:
              "Assign a clear owner for each priority with specific deadlines and success measures",
          },
          {
            step: "Connect the Dots",
            action:
              "Find the 3-5 places where teams must work together and create clear coordination plans",
          },
          {
            step: "Weekly Check-ins",
            action:
              "Start weekly meetings where teams share progress, identify problems, and adjust together",
          },
        ],
        growth_insight:
          "When everyone sees how their work connects to the bigger picture and to each other, results multiply. The key is deliberate structure, clear communication, and shared accountability.",
      };
    } else {
      return {
        icon: <EmojiEvents style={{ fontSize: "4rem" }} />,
        title: "BE IT: Embodying Sustainability & Culture",
        subtitle: "Your Primary Focus Area",
        description:
          "You can start strong, but keeping things going is the challenge. Changes don't stick, your team falls back into old habits, and success depends too much on a few key people. Making improvements last is your biggest opportunity.",
        symptoms: [
          "New initiatives start strong but fade after a few months",
          "Always pushing to maintain results instead of things running smoothly",
          "Too dependent on a few key people to keep things working",
          "Company values posted but not lived day-to-day",
          "Exhausted from constantly fighting to maintain performance",
        ],
        impacts: [
          "Can't grow because success depends on a few heroes",
          "Losing good people when they see values aren't real",
          "Competitors pulling ahead with stronger systems",
          "Leadership burnout from fighting the same battles repeatedly",
          "Missing opportunities because too busy maintaining what you have",
        ],
        solutions: [
          "Culture Check: Assess the gap between your stated values and daily reality",
          "Leadership Development: Build leaders at all levels who can maintain high performance",
          "Document Systems: Turn successful practices into repeatable processes anyone can follow",
          "Recognize Good Behavior: Celebrate people who demonstrate your values and get results",
          "Continuous Improvement: Make learning and adapting a regular habit, not a one-time event",
        ],
        nextSteps: [
          {
            step: "Culture Assessment",
            action:
              "In 30 days, get honest feedback on the gap between your stated values and daily reality",
          },
          {
            step: "Define Behaviors",
            action:
              "For each core value, write 3-5 specific behaviors you want to see and build them into hiring and reviews",
          },
          {
            step: "Build Systems",
            action:
              "Identify critical processes that depend on specific people and create guides or training to reduce dependency",
          },
          {
            step: "Develop Leaders",
            action:
              "Start a leadership program focused on coaching and creating accountability without micromanaging",
          },
        ],
        growth_insight:
          "Real change happens when new ways of working become 'just how we do things here' instead of special projects. Build culture and systems that make excellence the easy path, not the hard one.",
      };
    }
  };

  const focusDetails = getFocusDetails();

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #152A53 100%)",
        padding: isMobile ? "2rem 1rem" : "3rem 2rem",
      }}
    >
      <div
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
        }}
      >
        {/* Header */}
        <div
          style={{
            textAlign: "center",
            marginBottom: "3rem",
            opacity: animationStep >= 1 ? 1 : 0,
            transform:
              animationStep >= 1 ? "translateY(0)" : "translateY(20px)",
            transition: "all 0.6s ease-out",
          }}
        >
          <h1
            style={{
              fontSize: isMobile ? "2rem" : "3rem",
              fontWeight: "700",
              color: "white",
              marginBottom: "1rem",
            }}
          >
            Your Personal Reinvention Focus
          </h1>
          <p
            style={{
              fontSize: isMobile ? "1rem" : "1.25rem",
              color: "rgba(255, 255, 255, 0.9)",
            }}
          >
            Based on your responses, here's where to focus your energy
          </p>
        </div>

        {/* Scores Overview */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
            gap: "1.5rem",
            marginBottom: "3rem",
            opacity: animationStep >= 2 ? 1 : 0,
            transform:
              animationStep >= 2 ? "translateY(0)" : "translateY(20px)",
            transition: "all 0.6s ease-out 0.2s",
          }}
        >
          {[
            {
              type: "seeIt",
              label: "SEE IT (Clarity)",
              icon: <Lightbulb />,
              color: "#c9a750",
            },
            {
              type: "bridgeIt",
              label: "BRIDGE IT (Alignment)",
              icon: <ConnectWithoutContact />,
              color: "#c9a750",
            },
            {
              type: "beIt",
              label: "BE IT (Sustainability)",
              icon: <EmojiEvents />,
              color: "#c9a750",
            },
          ].map((item) => (
            <div
              key={item.type}
              style={{
                backgroundColor: "white",
                borderRadius: "16px",
                padding: "2rem",
                boxShadow: "0 10px 30px rgba(0, 0, 0, 0.2)",
                border:
                  (item.type === "seeIt" && primaryFocus === "SEE IT") ||
                  (item.type === "bridgeIt" && primaryFocus === "BRIDGE IT") ||
                  (item.type === "beIt" && primaryFocus === "BE IT")
                    ? `3px solid ${item.color}`
                    : "3px solid transparent",
                position: "relative",
              }}
            >
              {((item.type === "seeIt" && primaryFocus === "SEE IT") ||
                (item.type === "bridgeIt" && primaryFocus === "BRIDGE IT") ||
                (item.type === "beIt" && primaryFocus === "BE IT")) && (
                <div
                  style={{
                    position: "absolute",
                    top: "-12px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    backgroundColor: item.color,
                    color: "white",
                    padding: "0.5rem 1rem",
                    borderRadius: "20px",
                    fontSize: "0.75rem",
                    fontWeight: "700",
                    textTransform: "uppercase",
                    whiteSpace: "nowrap",
                  }}
                >
                  Primary Focus
                </div>
              )}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "1rem",
                  color: item.color,
                }}
              >
                {item.icon}
                <h3
                  style={{
                    fontSize: "1rem",
                    fontWeight: "600",
                    marginLeft: "0.5rem",
                    color:
                      (item.type === "seeIt" && primaryFocus === "SEE IT") ||
                      (item.type === "bridgeIt" &&
                        primaryFocus === "BRIDGE IT") ||
                      (item.type === "beIt" && primaryFocus === "BE IT")
                        ? item.color
                        : "#1a202c",
                  }}
                >
                  {item.label}
                </h3>
              </div>
              <div
                style={{
                  fontSize: "2.5rem",
                  fontWeight: "700",
                  color: item.color,
                  marginBottom: "0.5rem",
                }}
              >
                {item.type === "seeIt"
                  ? totalScores.seeIt
                  : item.type === "bridgeIt"
                  ? totalScores.bridgeIt
                  : totalScores.beIt}
              </div>
              <div
                style={{
                  fontSize: "0.875rem",
                  color: "#718096",
                }}
              >
                Total Score
              </div>
            </div>
          ))}
        </div>

        {/* Primary Focus Details */}
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "20px",
            padding: isMobile ? "2rem" : "3rem",
            marginBottom: "2rem",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
            opacity: animationStep >= 3 ? 1 : 0,
            transform:
              animationStep >= 3 ? "translateY(0)" : "translateY(20px)",
            transition: "all 0.6s ease-out 0.4s",
          }}
        >
          {/* Focus Header */}
          <div
            style={{
              textAlign: "center",
              marginBottom: "3rem",
              padding: "2rem",
              backgroundColor: "#fefce8",
              borderRadius: "16px",
            }}
          >
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: "80px",
                height: "80px",
                borderRadius: "50%",
                backgroundColor: focusColor,
                color: "white",
                marginBottom: "1rem",
              }}
            >
              {focusDetails.icon}
            </div>
            <h2
              style={{
                fontSize: isMobile ? "1.75rem" : "2.5rem",
                fontWeight: "700",
                color: focusColor,
                marginBottom: "0.5rem",
              }}
            >
              {focusDetails.title}
            </h2>
            <p
              style={{
                fontSize: "1.25rem",
                color: "#4a5568",
                fontWeight: "600",
              }}
            >
              {focusDetails.subtitle}
            </p>
          </div>

          {/* Description */}
          <div style={{ marginBottom: "3rem" }}>
            <p
              style={{
                fontSize: "1.125rem",
                lineHeight: "1.8",
                color: "#2d3748",
              }}
            >
              {focusDetails.description}
            </p>
          </div>

          {/* Card with 2x2 Grid for Symptoms & Impact, then Solutions */}
          <div
            style={{
              backgroundColor: "#f8fafc",
              borderRadius: "16px",
              padding: isMobile ? "1.5rem" : "2rem",
              marginBottom: "3rem",
              boxShadow: "0 10px 30px rgba(0, 0, 0, 0.1)",
            }}
          >
            {/* 2x2 Grid for Symptoms and Impacts */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: "2rem",
                marginBottom: "2rem",
              }}
            >
              {/* Key Symptoms */}
              <div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "1rem",
                  }}
                >
                  <Warning
                    style={{
                      color: "#f59e0b",
                      fontSize: "1.5rem",
                      marginRight: "0.5rem",
                    }}
                  />
                  <h3
                    style={{
                      fontSize: "1.25rem",
                      fontWeight: "700",
                      color: "#1a202c",
                    }}
                  >
                    What You Might Feel
                  </h3>
                </div>
                <div>
                  {focusDetails.symptoms.map((symptom, index) => (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        marginBottom: "0.75rem",
                      }}
                    >
                      <span
                        style={{
                          color: focusColor,
                          marginRight: "0.75rem",
                          fontWeight: "700",
                        }}
                      >
                        •
                      </span>
                      <span
                        style={{
                          fontSize: "0.95rem",
                          lineHeight: "1.6",
                          color: "#4a5568",
                        }}
                      >
                        {symptom}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Business Impact */}
              <div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "1rem",
                  }}
                >
                  <TrendingUp
                    style={{
                      color: "#ef4444",
                      fontSize: "1.5rem",
                      marginRight: "0.5rem",
                    }}
                  />
                  <h3
                    style={{
                      fontSize: "1.25rem",
                      fontWeight: "700",
                      color: "#1a202c",
                    }}
                  >
                    The Cost
                  </h3>
                </div>
                <div>
                  {focusDetails.impacts.map((impact, index) => (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        marginBottom: "0.75rem",
                      }}
                    >
                      <span
                        style={{
                          color: "#ef4444",
                          marginRight: "0.75rem",
                          fontWeight: "700",
                        }}
                      >
                        •
                      </span>
                      <span
                        style={{
                          fontSize: "0.95rem",
                          lineHeight: "1.6",
                          color: "#4a5568",
                        }}
                      >
                        {impact}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Recommended Solutions - Full Width */}
            <div
              style={{
                borderTop: "1px solid #e2e8f0",
                paddingTop: "2rem",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "1rem",
                }}
              >
                <CheckCircle
                  style={{
                    color: focusColor,
                    fontSize: "1.5rem",
                    marginRight: "0.5rem",
                  }}
                />
                <h3
                  style={{
                    fontSize: "1.25rem",
                    fontWeight: "700",
                    color: "#1a202c",
                  }}
                >
                  How to Fix It
                </h3>
              </div>
              <div>
                {focusDetails.solutions.map((solution, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      marginBottom: "0.75rem",
                    }}
                  >
                    <span
                      style={{
                        color: focusColor,
                        marginRight: "0.75rem",
                        fontWeight: "700",
                      }}
                    >
                      {index + 1}.
                    </span>
                    <span
                      style={{
                        fontSize: "0.95rem",
                        lineHeight: "1.6",
                        color: "#4a5568",
                      }}
                    >
                      {solution}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <div
            style={{
              backgroundColor: "#fefce8",
              borderRadius: "16px",
              padding: "2rem",
              marginBottom: "2rem",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "1rem",
              }}
            >
              <ArrowForward
                style={{
                  color: focusColor,
                  fontSize: "1.5rem",
                  marginRight: "0.5rem",
                }}
              />
              <h3
                style={{
                  fontSize: "1.5rem",
                  fontWeight: "700",
                  color: "#1a202c",
                }}
              >
                Your Next Steps
              </h3>
            </div>
            <div style={{ paddingLeft: "2rem" }}>
              {focusDetails.nextSteps.map((step, index) => (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    marginBottom: "1.5rem",
                  }}
                >
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      backgroundColor: focusColor,
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.875rem",
                      fontWeight: "700",
                      flexShrink: 0,
                      marginRight: "0.75rem",
                    }}
                  >
                    {index + 1}
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: "1.125rem",
                        fontWeight: "600",
                        color: "#1a202c",
                        marginBottom: "0.25rem",
                      }}
                    >
                      {step.step}
                    </div>
                    <p
                      style={{
                        fontSize: "1rem",
                        lineHeight: "1.6",
                        color: "#2d3748",
                        margin: 0,
                      }}
                    >
                      {step.action}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Key Insight */}
          <div
            style={{
              backgroundColor: "#fefce8",
              borderRadius: "16px",
              padding: "2rem",
              border: `2px solid ${focusColor}`,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "1rem",
              }}
            >
              <Info
                style={{
                  color: focusColor,
                  fontSize: "1.5rem",
                  marginRight: "0.5rem",
                }}
              />
              <h3
                style={{
                  fontSize: "1.25rem",
                  fontWeight: "700",
                  color: "#1a202c",
                }}
              >
                Remember This
              </h3>
            </div>
            <p
              style={{
                fontSize: "1.125rem",
                color: "#1a202c",
                lineHeight: "1.8",
                fontStyle: "italic",
                margin: 0,
              }}
            >
              "{focusDetails.growth_insight}"
            </p>
          </div>
        </div>

        {/* Call to Action */}
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "20px",
            padding: isMobile ? "2rem" : "3rem",
            textAlign: "center",
            marginBottom: "2rem",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
          }}
        >
          <h3
            style={{
              fontSize: isMobile ? "1.5rem" : "2rem",
              fontWeight: "700",
              color: "#1a202c",
              marginBottom: "1rem",
            }}
          >
            Ready to Make This Growth Happen?
          </h3>
          <p
            style={{
              fontSize: "1.125rem",
              color: "#4a5568",
              marginBottom: "2rem",
              lineHeight: "1.6",
            }}
          >
            Get personalized support to accelerate your{" "}
            {focusDetails.title.toLowerCase()} journey.
          </p>
          <div
            style={{
              display: "flex",
              gap: "1rem",
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <a
              href="https://intheraconsultinggroup.com/"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                padding: "1rem 2rem",
                fontSize: "1.1rem",
                fontWeight: "600",
                color: "white",
                background: focusColor,
                border: "none",
                borderRadius: "12px",
                cursor: "pointer",
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
                boxShadow: "0 4px 15px rgba(201, 167, 80, 0.4)",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 6px 20px rgba(201, 167, 80, 0.5)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                  "0 4px 15px rgba(201, 167, 80, 0.4)";
              }}
            >
              Join the R.E.A.L Circle
              <ArrowForward />
            </a>
            <button
              onClick={onRestart}
              style={{
                padding: "1rem 2rem",
                fontSize: "1.1rem",
                fontWeight: "600",
                color: focusColor,
                background: "white",
                border: `2px solid ${focusColor}`,
                borderRadius: "12px",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#fefce8";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "white";
              }}
            >
              Retake Assessment
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GrowthResults;
